import { useState, useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home, Users, UserCheck, Settings, LogOut, Menu, X,
  ChevronsLeft, Sun, Moon,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useAuthorization } from '@/hooks/useAuthorization'
import { useTheme } from '../contexts/ThemeContext'
import { getInitials } from '../lib/utils'

interface LayoutProps { children: React.ReactNode }

const primaryNav = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Persons',   href: '/persons',   icon: Users },
] as const

const adminNav = [
  { name: 'Pending Approvals', href: '/admin/users/pending', icon: UserCheck, adminOnly: false },
  { name: 'User Management',   href: '/admin/users',          icon: Settings,  adminOnly: true  },
] as const

// Derive breadcrumb segments from the current path. Kept simple — the deeper
// "Persons / Name" path is filled in by PersonDetailPage itself once the data
// loads (it can replace the breadcrumb via a portal in a later phase).
function useBreadcrumb() {
  const { pathname } = useLocation()
  return useMemo(() => {
    const map: Array<{ match: RegExp; icon: typeof Home; label: string }> = [
      { match: /^\/dashboard/,           icon: Home,      label: 'Dashboard' },
      { match: /^\/persons\/new/,        icon: Users,     label: 'Persons / New' },
      { match: /^\/persons\/\d+\/edit/,  icon: Users,     label: 'Persons / Edit' },
      { match: /^\/persons\/\d+/,        icon: Users,     label: 'Persons' },
      { match: /^\/persons/,             icon: Users,     label: 'Persons' },
      { match: /^\/biodata/,             icon: Users,     label: 'Biodata' },
      { match: /^\/photos/,              icon: Users,     label: 'Photos' },
      { match: /^\/media-release-forms/, icon: Users,     label: 'Media Release Forms' },
      { match: /^\/admin\/users\/pending/, icon: UserCheck, label: 'Pending Approvals' },
      { match: /^\/admin\/users/,        icon: Settings,  label: 'User Management' },
    ]
    return map.find(m => m.match.test(pathname)) ?? { icon: Home, label: 'BK Profiles' }
  }, [pathname])
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const { isAdmin, canApproveUsers } = useAuthorization()
  const { resolvedTheme, setTheme } = useTheme()
  const location = useLocation()

  const [sideCollapsed, setSideCollapsed] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.localStorage.getItem('sidebarCollapsed') === '1')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [avatarFailed, setAvatarFailed] = useState(false)

  useEffect(() => {
    window.localStorage.setItem('sidebarCollapsed', sideCollapsed ? '1' : '0')
  }, [sideCollapsed])

  // Close mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  const visibleAdminNav = adminNav.filter(i => isAdmin || !i.adminOnly)
  const showAdminSection = isAdmin || canApproveUsers

  // Longest-prefix wins for admin highlight — see prior implementation note.
  const activeAdminHref = visibleAdminNav
    .filter(i => location.pathname === i.href || location.pathname.startsWith(i.href + '/'))
    .sort((a, b) => b.href.length - a.href.length)[0]?.href

  const isPrimaryActive = (href: string) =>
    location.pathname === href || (href !== '/dashboard' && location.pathname.startsWith(href))

  return (
    <div className="bk-app" data-side-collapsed={sideCollapsed}>
      {/* Mobile backdrop */}
      <div className="bk-mobile-bd" data-open={mobileOpen} onClick={() => setMobileOpen(false)} />

      {/* Sidebar */}
      <aside className="bk-side" data-open={mobileOpen}>
        <div className="bk-side-hd">
          <img
            src="/images/bk-profile-logo.png"
            alt="BK Profiles"
            style={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }}
          />
          <div className="bk-wordmark">
            BK Profiles
            <span className="sub">System</span>
          </div>
          <button
            type="button"
            className="bk-side-toggle"
            onClick={() => setSideCollapsed(v => !v)}
            aria-label={sideCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sideCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronsLeft size={14} />
          </button>
        </div>

        <nav className="bk-side-nav" aria-label="Primary">
          <div className="bk-nav-label">Workspace</div>
          {primaryNav.map(item => {
            const Icon = item.icon
            const active = isPrimaryActive(item.href)
            return (
              <Link
                key={item.href}
                to={item.href}
                className="bk-nav-item"
                data-active={active}
                title={sideCollapsed ? item.name : undefined}
              >
                <Icon size={16} />
                <span className="bk-nav-label-txt">{item.name}</span>
              </Link>
            )
          })}

          {showAdminSection && (
            <>
              <div className="bk-nav-label">Admin</div>
              {visibleAdminNav.map(item => {
                const Icon = item.icon
                const active = item.href === activeAdminHref
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="bk-nav-item"
                    data-active={active}
                    title={sideCollapsed ? item.name : undefined}
                  >
                    <Icon size={16} />
                    <span className="bk-nav-label-txt">{item.name}</span>
                  </Link>
                )
              })}
            </>
          )}
        </nav>

        <div className="bk-side-ft">
          {user?.avatar_url && !avatarFailed ? (
            <img
              className="bk-avatar bk-avatar-sm"
              src={user.avatar_url}
              alt={user.display_name}
              referrerPolicy="no-referrer"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <div className="bk-avatar bk-avatar-sm bk-avatar-c0">
              {getInitials(user?.display_name || 'U')}
            </div>
          )}
          <div className="meta">
            <div className="name">{user?.display_name}</div>
            <div className="email">{user?.email}</div>
          </div>
          <button
            type="button"
            className="bk-icon-btn bk-icon-btn-sm"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title="Toggle theme"
          >
            {resolvedTheme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <button
            type="button"
            className="bk-icon-btn bk-icon-btn-sm"
            onClick={() => { void logout() }}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>

        <div className="bk-side-version" title={`Built ${new Date(__APP_BUILD_TIME__).toLocaleString()}`}>
          v{__APP_VERSION__} ({__APP_COMMIT__})
        </div>
      </aside>

      {/* Main column */}
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Mobile bar */}
        <header className="bk-mobile-bar">
          <button
            type="button"
            className="bk-icon-btn"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <img
            src="/images/bk-profile-logo.png"
            alt="BK Profiles"
            style={{ height: 28, objectFit: 'contain' }}
          />
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="bk-icon-btn"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            aria-label="Toggle theme"
          >
            {resolvedTheme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </header>

        <main style={{ flex: 1, minWidth: 0 }}>
          {children}
        </main>
      </div>

      {/* Close-drawer button shown only inside the open drawer on mobile */}
      {mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          style={{
            position: 'fixed', top: 12, left: 'calc(var(--bk-side-w) - 44px)',
            zIndex: 31, background: 'transparent', border: 0, color: 'var(--bk-ink-2)', cursor: 'pointer',
          }}
        >
          <X size={18} />
        </button>
      )}
    </div>
  )
}
